import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import * as d3 from "d3";
import {
  BarChart,
  Bar,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

// Score bar component
const ScoreBar = ({ label, score, maxScore = 10 }) => (
  <div className="mb-4">
    <div className="mb-1 flex justify-between text-sm">
      <span className="text-gray-700">{label}</span>
      <span className="font-medium text-gray-900">{score}</span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full bg-green-500"
        style={{
          width: `${(score / maxScore) * 100}%`,
          transition: 'width 0.5s ease-in-out'
        }}
      />
    </div>
  </div>
);

// Distribution chart component using recharts
const CustomDistributionChart = ({ data }) => {
  const chartData = data.map((value, index) => ({
    name: index === 0 ? "Min" : index === data.length - 1 ? "Max" : `Q${index}`,
    value: value,
  }));

  return (
    <div className="h-32 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
        >
          <XAxis dataKey="name" />
          <YAxis domain={[0, 100]} />
          <Tooltip />
          <Bar dataKey="value" fill="#4979D1" />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#2563EB"
            strokeWidth={2}
            dot={{ fill: "#2563EB" }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const placeholderScores = {
  "Syntax-Semantics": 4,
  "Temporal Pattern": 10,
  "Fluency and Filler words": 10,
  "Vocabulary Richness": 10,
  "Discourse Cohesion": 7,
};

// Horizontal Box component for boxplot
const HorizontalBox = ({
  min,
  q1,
  median,
  q3,
  max,
  height,
  stroke,
  fill,
  userScore,
}) => {
  const STROKE_WIDTH = 1.5;

  return (
    <>
      {/* Whiskers and box */}
      <line
        y1={height / 2}
        y2={height / 2}
        x1={min}
        x2={max}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      <rect
        y={0}
        x={q1}
        height={height}
        width={q3 - q1}
        stroke={stroke}
        fill={fill}
        fillOpacity={0.3}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        y1={0}
        y2={height}
        x1={median}
        x2={median}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        y1={height * 0.25}
        y2={height * 0.75}
        x1={min}
        x2={min}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        y1={height * 0.25}
        y2={height * 0.75}
        x1={max}
        x2={max}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      {/* User score indicator */}
      <circle
        cx={userScore}
        cy={height / 2}
        r={6}
        fill="#FF6B6B"
        stroke="white"
        strokeWidth={2}
      />
    </>
  );
};

// Dummy data for the distribution
const dummyDistributionData = {
  min: 45,
  q1: 65,
  median: 75,
  q3: 85,
  max: 95,
  userScore: 82,
};

// Custom Boxplot component
const CustomBoxplot = ({ width, height }) => {
  const margin = { top: 20, right: 20, left: 40, bottom: 20 };
  const boundsWidth = width - margin.left - margin.right;
  const boundsHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(() => {
    return d3.scaleLinear().domain([0, 100]).range([0, boundsWidth]);
  }, [boundsWidth]);

  // X axis ticks
  const xTicks = xScale.ticks(5).map((tick) => ({
    value: tick,
    xOffset: xScale(tick),
  }));

  // Scale the distribution values
  const scaledData = {
    min: xScale(dummyDistributionData.min),
    q1: xScale(dummyDistributionData.q1),
    median: xScale(dummyDistributionData.median),
    q3: xScale(dummyDistributionData.q3),
    max: xScale(dummyDistributionData.max),
    userScore: xScale(dummyDistributionData.userScore),
  };

  return (
    <svg width={width} height={height}>
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {/* X axis */}
        <g transform={`translate(0, ${boundsHeight})`}>
          {xTicks.map(({ value, xOffset }) => (
            <g key={value} transform={`translate(${xOffset}, 0)`}>
              <line y1={0} y2={5} stroke="#6b7280" strokeWidth={0.5} />
              <text y={20} textAnchor="middle" fontSize={10} fill="#6b7280">
                {value}
              </text>
            </g>
          ))}
        </g>

        {/* Distribution labels */}
        <text
          x={-10}
          y={boundsHeight / 2}
          textAnchor="end"
          fontSize={12}
          fill="#4B5563"
          dy=".32em"
        >
          Score Distribution
        </text>

        {/* Boxplot */}
        <g transform={`translate(0, ${boundsHeight / 3})`}>
          <HorizontalBox
            min={scaledData.min}
            q1={scaledData.q1}
            median={scaledData.median}
            q3={scaledData.q3}
            max={scaledData.max}
            height={40}
            stroke="#4979D1"
            fill="#4979D1"
            userScore={scaledData.userScore}
          />
        </g>

        {/* Legend */}
        <g transform={`translate(${boundsWidth - 100}, 10)`}>
          <circle
            cx={0}
            cy={0}
            r={6}
            fill="#FF6B6B"
            stroke="white"
            strokeWidth={2}
          />
          <text x={12} y={0} dy=".32em" fontSize={10} fill="#4B5563">
            Your Score
          </text>
        </g>
      </g>
    </svg>
  );
};

/**
 * ReportSummaryPage: displays patient overview, report summary, observations, recommendations, and performance.
 */
export default function ReportSummaryPage({ report = {}, taskData = [] }) {
  const {
    summary = "",
    observations = [],
    recommendations = [],
    distribution = [65, 72, 78, 85, 90],
  } = report;

  // Calculate average scores from all tasks
  const averageScores = useMemo(() => {
    if (!taskData || taskData.length === 0) return {};

    const scoreCategories = {};
    let totalTasks = taskData.length;

    // Sum up scores for each category
    taskData.forEach((task) => {
      Object.entries(task.scores).forEach(([category, score]) => {
        // Only scale 'Correctness' from 0-1 to 0-10
        const displayScore =
          category === "Correctness Score" &&
          typeof score === "number" &&
          score <= 1 &&
          score >= 0
            ? score * 10
            : score;
        if (!scoreCategories[category]) {
          scoreCategories[category] = 0;
        }
        scoreCategories[category] += displayScore;
      });
    });

    // Calculate averages
    const averages = Object.entries(scoreCategories).reduce(
      (acc, [category, total]) => {
        acc[category] = Math.round((total / totalTasks) * 10) / 10; // Round to 1 decimal
        return acc;
      },
      {},
    );

    return averages;
  }, [taskData]);

  // Calculate overall score (average of all category averages)
  const overallScore = useMemo(() => {
    if (Object.keys(averageScores).length === 0) return 0;
    const total = Object.values(averageScores).reduce(
      (sum, score) => sum + score,
      0,
    );
    return Math.round((total / Object.keys(averageScores).length) * 10) / 10;
  }, [averageScores]);

  return (
    <div className="h-screen bg-[#4979D1] p-5">
      <div className="flex h-[calc(100vh-40px)] gap-5">
        {/* Main content */}
        <div className="flex-1 overflow-y-auto rounded-lg bg-white p-8">
          <div className="max-w-3xl">
            <h1 className="mb-8 text-left text-2xl font-bold">
              Report summary
            </h1>
            <p className="mb-12 text-left text-lg">{summary}</p>

            <div className="mb-12">
              <h2 className="mb-4 text-left text-xl font-bold">
                Key observations include:
              </h2>
              <ul className="space-y-4 text-left">
                {observations.map((obs, index) => (
                  <li key={index}>
                    <span className="font-bold">{obs.title}: </span>
                    <span>{obs.detail}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="mb-4 text-left text-xl font-bold">
                Recommendations
              </h2>
              <ul className="space-y-4 text-left">
                {recommendations.map((rec, index) => (
                  <li key={index}>
                    <span className="font-bold">{rec.title}: </span>
                    <span>{rec.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-96 rounded-lg bg-gray-50 p-8">
          <h2 className="mb-6 text-2xl font-bold text-gray-800">
            Overall Performance
          </h2>
          <div className="flex flex-col items-center">
            <div className="h-40 w-40">
              <CircularProgressbar
                value={overallScore * 10}
                text={`${Math.round(overallScore * 10)}%`}
                styles={buildStyles({
                  textColor: "#222",
                  pathColor: "#4979D1",
                  trailColor: "#e5e7eb",
                  textSize: "20px",
                  pathTransitionDuration: 0.5,
                })}
              />
            </div>
            <div className="mt-2 text-center">
                <span className="text-lg font-bold text-neutral-700">
                  {overallScore >= 7
                    ? "Good"
                    : overallScore >= 5
                      ? "Average"
                      : "Needs Improvement"}
                </span>
              </div>
            {/* <div className="mt-2 h-32 w-full">
              <CustomBoxplot width={300} height={120} />
            </div> */}
            <div className="mt-4 w-full">
              <h3 className="mb-4 text-xl font-bold">Score Breakdown</h3>
              {Object.entries(averageScores).map(([label, score]) => {
                // Only scale 'Correctness' from 0-1 to 0-10 for display
                const displayScore =
                  label === "Correctness Score" &&
                  typeof score === "number" &&
                  score <= 1 &&
                  score >= 0
                    ? score * 10
                    : score;
                return (
                  <ScoreBar key={label} label={label} score={displayScore} />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ReportSummaryPage.propTypes = {
  report: PropTypes.shape({
    summary: PropTypes.string,
    observations: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        detail: PropTypes.string,
      }),
    ),
    recommendations: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        detail: PropTypes.string,
      }),
    ),
    distribution: PropTypes.array,
  }),
  taskData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      scores: PropTypes.object,
    }),
  ),
};
